import { ChangeEvent, useLayoutEffect, useRef, useState } from "react";
import { IEndpoints, useEndpoints } from "./useEndpoints";
import { useToastAlert } from "./useToastAlert/useToastAlert";
import { useImmer } from "use-immer";
import { useDebounce } from "./useDebounce";
import { v4 } from "uuid";

type CallableEndpointKeys = {
  [K in keyof IEndpoints]: IEndpoints[K] extends (...args: any) => any ? K : never;
}[keyof IEndpoints];

export const useAsyncTable = <
  T extends CallableEndpointKeys,
  L extends object = object,
  B  = Awaited<ReturnType<Awaited<IEndpoints[T]>>>["data"]
>(
  endpoint: T,
  options?: {
    search: boolean;
  },
  customParams?: L
) => {
  const [isLoading, setIsLoading] = useState(false);
  const [searchValue, setSearchValue] = useState("");
  const toastAlert = useToastAlert();
  const endpoints = useEndpoints();
  const id = useRef(v4());
  const { callbackDebounce } = useDebounce();
  const [params, setParams] = useImmer<L>(customParams || ({} as L));
  const [pagination, setPagination] = useImmer({
    page: 1,
    limit: 20,
  });
  const [data, setData] =
    useState<B>();

  const isEmptyData = useRef(false);

  const fetch = async () =>
    callbackDebounce(
      async () => {
        try {
          setIsLoading(true);
          const fetchParams = {
            ...(options?.search !== false && searchValue
              ? { search: searchValue }
              : {}),
            ...params,
            page: pagination.page,
            ...{ limit: pagination.limit },
          };

          const { data } = await (endpoints[endpoint] as any)(fetchParams);
          setData(data);
          isEmptyData.current =
            Object.keys(params).length === 0 &&
            !searchValue &&
            !data?.docs?.length &&
            pagination.page === 1;
        } catch (error) {
          toastAlert({
            type: "error",
            message: "Error load",
          });
        } finally {
          setIsLoading(false);
        }
      },
      id.current,
      200
    );

  useLayoutEffect(() => {
    fetch();
    // eslint-disable-next-line
  }, [pagination, params, searchValue]);

  const updateParam = <W extends keyof L>(key: W, value: L[W]) => {
    setParams((draft) => {
      if (
        (value === undefined || value === "" || value === null) &&
        key in draft
      ) {
        delete (draft as any)[key];
      } else {
        (draft as any)[key] = value;
      }
    });
  };

  const updatePagination = (key: keyof typeof pagination, value: number) => {
    setPagination((draft) => {
      draft[key] = value;
    });
  };
  const updateSearchValue = (
    e: ChangeEvent<HTMLInputElement | HTMLTextAreaElement>
  ) => {
    setSearchValue(e.target.value);
  };

  return {
    updateSearchValue,
    updatePagination,
    updateParam,
    searchValue,
    isLoading,
    fetch,
    data,
    pagination,
    isEmptyData,
    params,
  };
};

export default useAsyncTable;
